import _ from 'lodash';
import moment, { Moment } from 'moment-timezone';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { errorToast } from '../toast';

export const useAllQueryParamsReadonly = () => {
    const location = useLocation();
    return useMemo(() => queryString.parse(location.search), [location.search]);
};

export const useQueryParamReadonly = (key: string): string => {
    const queryParams = useAllQueryParamsReadonly();
    const rawValue = useMemo(() => queryParams[key], [key, queryParams]);
    const value = useMemo(
        () => (Array.isArray(rawValue) ? rawValue[0] : rawValue) ?? '',
        [rawValue],
    );
    return value;
};

// This function exists, as if you are trying to set two or more query parameters
//  in quick succession, the later ones will overwrite the previous calls.
//  This is because of a delay in how the location is updated; see
//  https://stackoverflow.com/a/55605426/1292652
// To combat this, you can update both keys at once.
export const useSetQueryParams = () => {
    const history = useHistory();
    const oldQueryParams = useAllQueryParamsReadonly();
    return useCallback(
        (queryParamsToAdd: Record<string, string>) => {
            const newQueryParams = { ...oldQueryParams, ...queryParamsToAdd };
            // Remove empty query parameters. They just clutter the URL.
            Object.keys(newQueryParams).forEach((key) => {
                // ESLint doesn't know deleting the key is safe.
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                if (newQueryParams[key] === '') delete newQueryParams[key];
            });
            history.push({
                search: queryString.stringify(newQueryParams),
            });
        },
        [oldQueryParams, history],
    );
};

export const useClearQueryParams = () => {
    const location = useLocation();
    const history = useHistory();
    return useCallback(
        () =>
            history.push({
                pathname: location.pathname,
                search: '',
            }),
        [history, location.pathname],
    );
};

export const useQueryParams = <Schema extends Record<string, string>>(
    defaultValues: Schema,
): [Schema, (newValues: Partial<Schema>) => void] => {
    const allQueryParams = useAllQueryParamsReadonly();
    const setQueryParams = useSetQueryParams();

    const keys = useMemo(() => Object.keys(defaultValues) as (keyof Schema)[], [defaultValues]);
    const values = useMemo(
        () =>
            _.chain(keys)
                .keyBy((key) => key)

                .mapValues(
                    // @ts-expect-error Typescript doesn't want use to index query params with dynamic keys.
                    (key) => allQueryParams[key] ?? defaultValues[key],
                )
                .value() as Schema,
        [allQueryParams, defaultValues, keys],
    );
    const setValues = useCallback(
        (newValues: Partial<Schema>) => {
            // @ts-expect-error TODO: Figure out why Typescript is complaining.
            setQueryParams(newValues);
        },
        [setQueryParams],
    );

    return [values, setValues];
};

export const useQueryParam = (
    key: string,
    defaultValue: string,
): [string, (value: string) => void] => {
    const setQueryParams = useSetQueryParams();
    const rawValue = useQueryParamReadonly(key);
    const value = rawValue || defaultValue;
    const setValue = useCallback(
        (newValue: string) => {
            setQueryParams({ [key]: newValue });
        },
        [key, setQueryParams],
    );
    useEffect(() => {
        if (rawValue === '' && defaultValue !== '') {
            setValue(defaultValue);
        }
    }, [defaultValue, rawValue, setValue]);
    return [value, setValue];
};

// TODO: Wrap with `useMemo`.
export const useDateQueryParam = (key: string): [Moment, (date: Moment) => void] => {
    const defaultDate: Moment = moment();
    const formatDate = (date: Moment) => date.format('YYYY-MM-DD');

    const [dateStr, setDateStr] = useQueryParam(key, formatDate(defaultDate));
    const dateStrIsValid = moment(dateStr).isValid();

    if (!dateStrIsValid) {
        errorToast("Invalid date in URL. Defaulting to today's date.");
        setDateStr(formatDate(defaultDate));
    }
    const [date, setDate] = useState(dateStrIsValid ? moment(dateStr) : defaultDate);

    const setDateAndDateStr = (date: Moment) => {
        setDate(date);
        setDateStr(formatDate(date));
    };
    return [date, setDateAndDateStr];
};

// TODO: Wrap with `useMemo`.
export const useStartAndEndDateQueryParams = () => {
    const [startDate, setStartDate] = useDateQueryParam('start_date');
    const [endDate, setEndDate] = useDateQueryParam('end_date');

    const isValid = endDate.isSameOrAfter(startDate);
    if (!isValid) {
        errorToast("Invalid dates in URL. Defaulting to today's date.");
        setStartDate(moment());
        setEndDate(moment());
    }

    return [isValid ? startDate : moment(), setStartDate, isValid ? endDate : moment(), setEndDate];
};
